// spec 260509-system-monitor D-1：系统监控前端色板（design.md §5.1）。
//
// 这一份是"系统监控这个 spec 的视觉 token 在 JS 侧的来源"。CSS 侧在
// ``frontend/src/styles/sysmon.css`` 的 ``:root`` 里有同步硬编码版（同 hex
// 值），由 dashboard / widget 各 ``.dash-*`` / ``.sysmon-*`` 类通过
// ``var(--sysmon-color-*)`` 引用。JS 侧（如 Sparkline 内联 stroke）import 这里
// 的常量。
//
// **代码侧 hardcoded palette mirror**：sysmon.css 的 :root 与本文件的
// ``SYSMON_COLORS`` 是手工同步的两份相同 hex；调色时**两边都要改**（patch
// D-3 决定不依赖 monitor.pen variables，因为 pen variables 当前为空）。token
// 测试 ``tokens.test.ts`` 锁住 hex 防止只改 JS 不改 CSS。
//
// 不重复定义全局应用色板（``frontend/src/styles/theme.css`` 自有 design
// system 体系）；本文件只覆盖系统监控独有的 ok / warn / crit / model / net-down /
// net-up 6 色。其他视觉变量（spacing、字号、radius）走全局 design system + 新
// sysmon.css 的局部样式。

import type { Severity } from './thresholds';

export const SYSMON_COLORS = {
  /** 健康态 / 正常 sparkline；与 design.md §5.1 同源。 */
  ok: '#22C55E',
  /** 警戒态 / 阈值染色（≥ warn）。 */
  warn: '#F59E0B',
  /** 危险态 / 阈值染色（≥ crit）。 */
  crit: '#EF4444',
  /** 模型主调（widget ③ 段、Grafana 跳转链接、模型卡边框）。 */
  model: '#A855F7',
  /** 网络下行（与 dashboard NetworkPanel 主曲线、磁盘读色保持一致）。 */
  netDown: '#3B82F6',
  /** 网络上行（dashboard NetworkPanel 副曲线、磁盘写色保持一致）。 */
  netUp: '#A78BFA',
} as const;

export type SysmonColorKey = keyof typeof SYSMON_COLORS;

/** 把 ``Severity`` 映射到颜色 hex；Sparkline / 内联 SVG 用。 */
export function severityToColor(s: Severity): string {
  switch (s) {
    case 'crit':
      return SYSMON_COLORS.crit;
    case 'warn':
      return SYSMON_COLORS.warn;
    case 'ok':
    default:
      return SYSMON_COLORS.ok;
  }
}

/**
 * CSS 自定义属性名清单。**已由 ``frontend/src/styles/sysmon.css`` 的 ``:root``
 * 真实定义**（patch D-1）；dashboard / widget 各 `.dash-*` / `.sysmon-*` 类
 * 都通过 ``color: var(--sysmon-color-*)`` 引用。
 *
 * 这个常量主要给 TS 侧"想动态注入色（如 inline ``style={{color: var()}}``）"
 * 时用；普通组件直接走 className 即可，不需要 import 它。
 */
export const SYSMON_CSS_VARS = {
  ok: '--sysmon-color-ok',
  warn: '--sysmon-color-warn',
  crit: '--sysmon-color-crit',
  model: '--sysmon-color-model',
  netDown: '--sysmon-color-net-down',
  netUp: '--sysmon-color-net-up',
} as const;
